/**
 * Maison Atlas — enrichissements du tiroir panier de Horizon.
 *
 * Trois ajouts de marque, greffes sur le tiroir natif, jamais en remplacement :
 *   - le titre du tiroir (sur-titre + libelle de marque) ;
 *   - une barre de progression vers la livraison offerte (.ma-shipping-bar) ;
 *   - un bloc de vente complementaire (.ma-upsell / .ma-pack-upsell).
 *
 * Session 2 (2026-08-21) — deux defauts corriges :
 *
 * D3. Horizon remplace le contenu de [data-hydration-key="cart-drawer-inner"]
 *     par morphing a chaque mise a jour du panier. Le script memorisait le
 *     dialogue a l'initialisation : apres un morphing, il ecrivait dans un
 *     noeud detache. Les blocs injectes disparaissaient, puis revenaient
 *     apres un aller-retour reseau — les deux etats de mise en page se
 *     croisaient a l'ecran. Desormais le dialogue est re-resolu a chaque
 *     acces, un MutationObserver detecte le remplacement et les blocs sont
 *     re-injectes immediatement a partir du dernier etat connu, sans
 *     attendre le reseau. Le rafraichissement des donnees suit, et ne fait
 *     que corriger des valeurs deja affichees.
 *
 * D5. Six handles produit etaient ecrits en dur ici. Ils viennent maintenant
 *     de sections/ma-cart-config.liquid, qui les resout au rendu Liquid.
 *     Ce fichier ne contient plus aucun handle litteral.
 *
 * Reste a corriger hors de cette session : handleUpsellAdd() poste vers
 * /cart/add.js sans le parametre `sections` et diffuse un detail incomplet.
 * C'est le quatrieme chemin d'ajout au panier releve en session 0.
 */

const MA_CFG = window.maisonAtlasConfig || {};
const MA_CART_CFG = MA_CFG.cart || {};
const MA_S = (window.maisonAtlasStrings && window.maisonAtlasStrings.cart) || {};
const MA_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

/** Identifie les evenements emis par ce script, pour ne pas y reagir. */
const SOURCE_ID = 'ma-cart-upsell';

const SHIPPING_THRESHOLD_CENTS = Number(MA_CFG.freeShippingThresholdCents) || 0;
const CURRENCY = MA_CFG.currency || 'EUR';

const asHandle = (value) => (typeof value === 'string' ? value.trim() : '');

const PACK_HANDLE = asHandle(MA_CART_CFG.packHandle);
const TRIGGER_HANDLE = asHandle(MA_CART_CFG.triggerHandle);
const UPSELL_HANDLES = Array.isArray(MA_CART_CFG.upsellHandles)
  ? MA_CART_CFG.upsellHandles.map(asHandle).filter(Boolean)
  : [];
const UPSELL_LIMIT = Number(MA_CART_CFG.upsellLimit) > 0 ? Number(MA_CART_CFG.upsellLimit) : 2;

const moneyFormatter = new Intl.NumberFormat(MA_CFG.locale || undefined, {
  style: 'currency',
  currency: CURRENCY,
});

const formatMoneyCents = (cents) => moneyFormatter.format((cents || 0) / 100);

const escapeHtml = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]
  );

const maText = (value) => escapeHtml(value || '');
const maAttr = (value) => String(value || '').replace(/"/g, '&quot;');

/** Remplace les jetons __XXX__ des chaines traduites. */
const maFill = (template, replacements) => {
  let output = String(template || '');
  for (const token in replacements) output = output.split(token).join(replacements[token]);
  return output;
};

// --- Diagnostic de configuration -------------------------------------------
// Une vente complementaire ne doit plus jamais disparaitre en silence.

if (Array.isArray(MA_CART_CFG.unresolvedHandles) && MA_CART_CFG.unresolvedHandles.length > 0) {
  console.warn(
    '[ma-cart-drawer] handles configures introuvables en boutique, ignores :',
    MA_CART_CFG.unresolvedHandles.join(', '),
    '— corrigez-les dans Personnaliser > MA · Config panier.'
  );
}

if (!PACK_HANDLE) {
  console.warn(
    '[ma-cart-drawer] aucun produit pack resolu : la suggestion de pack est desactivee.',
    'Selectionnez-le dans Personnaliser > MA · Config panier.'
  );
}

if (UPSELL_HANDLES.length === 0) {
  console.warn(
    '[ma-cart-drawer] aucun candidat de vente complementaire resolu :',
    'verifiez la collection de reference dans Personnaliser > MA · Config panier.'
  );
}

// --- Acces reseau ----------------------------------------------------------

async function fetchCart() {
  try {
    const response = await fetch(`${MA_ROOT}cart.js`, { headers: { Accept: 'application/javascript' } });
    if (!response.ok) {
      console.warn('[ma-cart-drawer] lecture du panier impossible, statut', response.status);
      return null;
    }
    return await response.json();
  } catch (error) {
    console.warn('[ma-cart-drawer] lecture du panier impossible', error);
    return null;
  }
}

async function fetchProductByHandle(handle) {
  if (!handle) return null;

  try {
    const response = await fetch(`${MA_ROOT}products/${handle}.js`, {
      headers: { Accept: 'application/javascript' },
    });

    if (!response.ok) {
      console.warn(
        `[ma-cart-drawer] produit « ${handle} » introuvable (statut ${response.status}).`,
        'Handle obsolete ou produit depublie : la suggestion correspondante est retiree.'
      );
      return null;
    }

    return await response.json();
  } catch (error) {
    console.warn(`[ma-cart-drawer] produit « ${handle} » : requete en echec`, error);
    return null;
  }
}

/** Premiere variante achetable, ou null si le produit est entierement epuise. */
const firstAvailableVariant = (product) => {
  if (!product || !Array.isArray(product.variants) || product.variants.length === 0) return null;
  return product.variants.find((variant) => variant && variant.available) || null;
};

/** Diffuse la mise a jour du panier, en se signant pour ne pas se repondre. */
function dispatchCartUpdate(cart) {
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: SOURCE_ID,
        data: { source: SOURCE_ID, itemCount: cart?.item_count },
      },
    })
  );
}

// --- Composant -------------------------------------------------------------

class MaisonAtlasCartEnhancements {
  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {Element | null} Hote observe, re-verifie a chaque reprise. */
  #host = null;

  /** Vrai pendant nos propres ecritures : neutralise l'observateur. */
  #applying = false;

  /** Dernier panier connu : permet une re-injection immediate, sans reseau. */
  #cart = null;

  /** handle -> produit, ou null si la boutique l'a refuse. */
  #productCache = new Map();

  /** Evite les rafraichissements concurrents. */
  #refreshing = false;
  #refreshQueued = false;

  init() {
    document.addEventListener('cart:update', this.#onCartUpdate);
    document.addEventListener('shopify:section:load', this.#onSectionLoad);

    if (window.customElements) {
      customElements.whenDefined('cart-drawer-component').then(() => this.attach());
    }

    this.attach();
  }

  /**
   * (Re)branche l'observateur sur le tiroir courant. Idempotent : appelable a
   * chaque evenement sans effet de bord.
   */
  attach() {
    const host = document.querySelector('cart-drawer-component');

    if (!host) {
      this.#teardown();
      return;
    }

    if (host !== this.#host) {
      this.#teardown();
      this.#host = host;
      this.#observer = new MutationObserver(this.#onMutation);
      this.#observer.observe(host, { childList: true, subtree: true });
      // DialogOpenEvent ne remonte pas : il faut ecouter sur l'hote lui-meme.
      host.addEventListener('dialog:open', this.#onDialogOpen);
    }

    this.#applyBranding();

    if (this.#isOpen()) this.refresh();
  }

  #teardown() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#host?.removeEventListener('dialog:open', this.#onDialogOpen);
    this.#host = null;
  }

  /**
   * Le dialogue est re-resolu a chaque acces : Horizon le remplace par
   * morphing, toute reference memorisee devient un noeud detache.
   */
  #dialog() {
    return document.querySelector('cart-drawer-component dialog.cart-drawer__dialog');
  }

  #isOpen() {
    return Boolean(this.#dialog()?.open);
  }

  #onDialogOpen = () => {
    this.#applyBranding();
    this.refresh();
  };

  #onSectionLoad = () => {
    this.attach();
  };

  #onCartUpdate = (event) => {
    const detail = event?.detail || {};

    // Nos propres diffusions : deja prises en compte a la source.
    if (detail.sourceId === SOURCE_ID || detail.data?.source === SOURCE_ID) return;

    this.attach();
    this.refresh();
  };

  #onMutation = () => {
    if (this.#applying) return;

    // Le tiroir a pu etre retire du document (editeur de theme, navigation).
    if (this.#host && !document.contains(this.#host)) {
      this.#teardown();
      this.attach();
      return;
    }

    // Le morphing vient d'effacer nos blocs : on les remet tout de suite, a
    // partir du dernier etat connu. Aucune attente reseau, donc aucun
    // croisement de mises en page.
    this.#applyBranding();
  };

  /**
   * Applique titre et blocs de marque, de facon synchrone et idempotente.
   * Rien n'est cree si le bloc existe deja dans le dialogue COURANT.
   */
  #applyBranding() {
    const dialog = this.#dialog();
    if (!dialog) return;

    this.#applying = true;

    try {
      this.#brandHeading(dialog);
      this.#renderShippingBar(dialog);
      this.#renderPromo(dialog);
      this.#renderUpsell(dialog);
    } catch (error) {
      console.warn('[ma-cart-drawer] application des blocs de marque interrompue', error);
    } finally {
      // Purge les mutations que l'on vient de provoquer : sans cela, le
      // callback asynchrone de l'observateur les verrait apres la remise a
      // false du drapeau, et se rappellerait indefiniment.
      this.#observer?.takeRecords();
      this.#applying = false;
    }
  }

  /**
   * Titre de marque. Sans marqueur data-*, qui disparaissait avec le noeud a
   * chaque morphing : l'idempotence vient de la comparaison du contenu.
   */
  #brandHeading(dialog) {
    const heading = dialog.querySelector('.cart-drawer__heading');
    if (!heading) return;

    const label = MA_S.drawerHeading || '';
    const overtitle = MA_S.drawerOvertitle || '';

    if (label) {
      const textNode = [...heading.childNodes].find(
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0
      );

      if (textNode && textNode.textContent.trim() !== label.trim()) {
        textNode.textContent = label;
      }
    }

    if (overtitle && !heading.querySelector('.ma-cart-overtitle')) {
      const element = document.createElement('span');
      element.className = 'ma-cart-overtitle';
      element.textContent = overtitle;
      heading.prepend(element);
    }
  }

  #renderShippingBar(dialog) {
    const existing = dialog.querySelector('.ma-shipping-bar');

    // Seuil non configure : pas de barre, et surtout pas de barre a 100 %
    // annoncant une livraison offerte qui n'existe pas.
    if (!SHIPPING_THRESHOLD_CENTS) {
      existing?.remove();
      return;
    }

    const cart = this.#cart;
    const itemCount = cart?.item_count || 0;

    // Panier vide ou pas encore lu : rien a afficher.
    if (!cart || itemCount === 0) {
      existing?.remove();
      return;
    }

    const totalCents = cart.total_price || 0;
    const remainingCents = Math.max(0, SHIPPING_THRESHOLD_CENTS - totalCents);
    const percent = Math.min(100, Math.round((totalCents / SHIPPING_THRESHOLD_CENTS) * 100));
    const reached = remainingCents <= 0;

    let bar = existing;

    if (!bar) {
      bar = document.createElement('div');
      bar.className = 'ma-shipping-bar';
      bar.innerHTML = `<p class="ma-shipping-bar__label" role="status" aria-live="polite"></p><div class="ma-shipping-bar__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" aria-label="${maAttr(
        MA_S.shippingProgressLabel
      )}"><div class="ma-shipping-bar__fill"></div></div>`;

      this.#insertBlock(dialog, bar, dialog.querySelector('.cart-drawer__summary'));
    }

    const label = bar.querySelector('.ma-shipping-bar__label');
    const track = bar.querySelector('.ma-shipping-bar__track');
    const fill = bar.querySelector('.ma-shipping-bar__fill');

    const nextHtml = reached
      ? MA_S.freeShippingReachedHtml || ''
      : maFill(MA_S.freeShippingRemainingHtml, { __AMOUNT__: formatMoneyCents(remainingCents) });

    if (label && label.innerHTML !== nextHtml) label.innerHTML = nextHtml;

    bar.classList.toggle('is-complete', reached);
    track?.setAttribute('aria-valuenow', String(percent));
    if (fill) fill.style.width = `${percent}%`;
  }

  /**
   * Garde-fou livraison offerte, sous le bloc « code de bienvenue ».
   *
   * Le bloc lui-meme est rendu en Liquid par snippets/cart-summary.liquid,
   * que le tiroir Horizon reutilise tel quel : il n'y a rien a injecter ici,
   * seulement l'avertissement, qui depend du total en cours.
   *
   * Shopify evalue la condition « livraison offerte a partir de X » sur le
   * total APRES remise. Un panier juste au-dessus du seuil repasse donc en
   * dessous une fois le code applique : le client economise le pourcentage
   * mais repaie les frais de port, et y perd. On calcule le montant a
   * ajouter pour que le panier reste au-dessus du seuil UNE FOIS la remise
   * deduite : (total + X) x (100 - p) / 100 >= seuil.
   */
  #renderPromo(dialog) {
    const block = dialog.querySelector('ma-welcome-code');
    if (!block) return;

    const existing = block.querySelector('.ma-promo-warning');
    const promo = MA_CFG.promo;
    const message = MA_S.promoShippingWarningHtml;

    if (
      !promo ||
      !promo.active ||
      !message ||
      MA_CFG.promoWarningEnabled === false ||
      !SHIPPING_THRESHOLD_CENTS
    ) {
      existing?.remove();
      return;
    }

    const total = this.#cart?.total_price || 0;
    const percent = Number(promo.percent) || 0;
    const afterDiscount = Math.round((total * (100 - percent)) / 100);

    // Rien a signaler si le panier est deja sous le seuil (la livraison
    // etait deja payante) ou s'il y reste apres remise.
    if (percent <= 0 || total < SHIPPING_THRESHOLD_CENTS || afterDiscount >= SHIPPING_THRESHOLD_CENTS) {
      existing?.remove();
      return;
    }

    const needed = Math.max(
      0,
      Math.ceil((SHIPPING_THRESHOLD_CENTS * 100) / (100 - percent)) - total
    );

    const html = maFill(message, { __AMOUNT__: formatMoneyCents(needed) });

    let warning = existing;

    if (!warning) {
      warning = document.createElement('p');
      warning.className = 'ma-promo-warning';
      warning.setAttribute('role', 'status');
      warning.setAttribute('aria-live', 'polite');
      block.appendChild(warning);
    }

    if (warning.innerHTML !== html) warning.innerHTML = html;
  }

  /**
   * Insere un bloc avant le recapitulatif, ou a defaut en fin de contenu.
   * @param {Element} dialog
   * @param {Element} block
   * @param {Element | null} summary
   */
  #insertBlock(dialog, block, summary) {
    if (summary) {
      summary.before(block);
      return;
    }

    const content = dialog.querySelector('.cart-drawer__content');
    if (content) content.appendChild(block);
  }

  #renderUpsell(dialog) {
    const cart = this.#cart;

    const upsell = dialog.querySelector('.ma-upsell:not(.ma-pack-upsell)');
    const packUpsell = dialog.querySelector('.ma-pack-upsell');

    // Panier vide ou inconnu : aucune suggestion.
    if (!cart || (cart.item_count || 0) === 0) {
      upsell?.remove();
      packUpsell?.remove();
      return;
    }

    const handlesInCart = new Set(
      (cart.items || []).map((item) => item.handle || item.product_handle || '').filter(Boolean)
    );

    // Le pack est deja au panier : plus rien a proposer.
    if (PACK_HANDLE && handlesInCart.has(PACK_HANDLE)) {
      upsell?.remove();
      packUpsell?.remove();
      return;
    }

    const triggerOnly =
      Boolean(TRIGGER_HANDLE) && handlesInCart.size === 1 && handlesInCart.has(TRIGGER_HANDLE);

    if (triggerOnly && PACK_HANDLE) {
      upsell?.remove();
      this.#renderPackSuggestion(dialog);
      return;
    }

    packUpsell?.remove();

    let candidates = UPSELL_HANDLES.filter((handle) => !handlesInCart.has(handle)).slice(
      0,
      UPSELL_LIMIT
    );

    if (candidates.length < UPSELL_LIMIT && PACK_HANDLE && !handlesInCart.has(PACK_HANDLE)) {
      candidates = [...candidates, PACK_HANDLE].slice(0, UPSELL_LIMIT);
    }

    if (candidates.length === 0) {
      upsell?.remove();
      return;
    }

    const products = this.#resolveFromCache(candidates);

    // Les produits pas encore en cache arrivent par ensureProducts, qui
    // rappellera cette methode. Au premier passage seulement.
    if (products.length === 0) {
      upsell?.remove();
      return;
    }

    let block = upsell;

    if (!block) {
      block = document.createElement('div');
      block.className = 'ma-upsell';
      block.innerHTML = `<details class="ma-upsell-details"><summary class="ma-upsell-summary"><span class="ma-upsell-summary-label">${maText(
        MA_S.upsellHeading
      )}</span><span class="ma-upsell-summary-chevron" aria-hidden="true">+</span></summary><ul class="ma-upsell__list"></ul></details>`;

      const shippingBar = dialog.querySelector('.ma-shipping-bar');

      if (shippingBar) {
        shippingBar.before(block);
      } else {
        this.#insertBlock(dialog, block, dialog.querySelector('.cart-drawer__summary'));
      }
    }

    const list = block.querySelector('.ma-upsell__list');
    if (!list) return;

    const signature = products.map((product) => product.handle).join('|');

    // Ne reconstruit la liste que si son contenu change reellement : evite de
    // refermer le <details> ouvert par la visiteuse a chaque mise a jour.
    if (list.dataset.maSignature === signature) return;
    list.dataset.maSignature = signature;
    list.innerHTML = '';

    products.forEach((product) => {
      const card = this.#buildCard(product);
      if (card) list.appendChild(card);
    });

    if (!list.children.length) block.remove();
  }

  #renderPackSuggestion(dialog) {
    const product = this.#productCache.get(PACK_HANDLE);

    // Pas encore lu, ou introuvable : ensureProducts a deja trace en console.
    if (!product) {
      if (this.#productCache.has(PACK_HANDLE)) dialog.querySelector('.ma-pack-upsell')?.remove();
      return;
    }

    const variant = firstAvailableVariant(product);

    if (!variant) {
      dialog.querySelector('.ma-pack-upsell')?.remove();
      return;
    }

    const priceCents = product.price || 0;
    const compareCents = product.compare_at_price || 0;
    const savingCents = Math.max(0, compareCents - priceCents);

    let block = dialog.querySelector('.ma-pack-upsell');

    if (!block) {
      block = document.createElement('div');
      block.className = 'ma-upsell ma-pack-upsell';

      const shippingBar = dialog.querySelector('.ma-shipping-bar');

      if (shippingBar) {
        shippingBar.before(block);
      } else {
        this.#insertBlock(dialog, block, dialog.querySelector('.cart-drawer__summary'));
      }
    }

    const signature = `${product.handle}|${priceCents}|${compareCents}|${variant.id}`;
    if (block.dataset.maSignature === signature) return;
    block.dataset.maSignature = signature;

    const safeTitle = escapeHtml(product.title);
    const safeUrl = escapeHtml(product.url);

    const imageMarkup = product.featured_image
      ? `<img src="${escapeHtml(product.featured_image)}" alt="${safeTitle}" loading="lazy">`
      : `<span class="ma-upsell__card-placeholder" aria-hidden="true">${escapeHtml(
          product.title.charAt(0)
        )}</span>`;

    const saveLine =
      savingCents > 0
        ? maFill(MA_S.packSaveHtml, {
            __PRICE__: formatMoneyCents(priceCents),
            __COMPARE__: formatMoneyCents(compareCents),
            __SAVING__: formatMoneyCents(savingCents),
          })
        : MA_S.packNoSave || '';

    block.innerHTML = `<div class="ma-pack-upsell__inner"><p class="ma-upsell-summary-label">${maText(
      MA_S.packUpsellHeading
    )}</p><ul class="ma-upsell__list"><li class="ma-upsell__card"><a href="${safeUrl}" class="ma-upsell__card-image" aria-label="${maAttr(
      maFill(MA_S.viewProduct, { __PRODUCT__: safeTitle })
    )}">${imageMarkup}</a><a href="${safeUrl}" class="ma-upsell__card-title-link"><div class="ma-upsell__card-body"><p class="ma-upsell__card-title">${safeTitle}</p><p class="ma-upsell__card-price">${saveLine}</p></div></a><button type="button" class="ma-upsell__card-button" data-variant-id="${
      variant.id
    }">${maText(MA_S.addShort)}</button></li></ul></div>`;

    block
      .querySelector('.ma-upsell__card-button')
      ?.addEventListener('click', (event) => this.handleUpsellAdd(event));
  }

  /**
   * Construit une carte de suggestion. Retourne null pour un produit sans
   * variante achetable : on ne propose jamais un soin epuise.
   */
  #buildCard(product) {
    const variant = firstAvailableVariant(product);

    if (!variant) {
      console.warn(
        `[ma-cart-drawer] produit « ${product.handle} » sans variante disponible : suggestion retiree.`
      );
      return null;
    }

    const item = document.createElement('li');
    item.className = 'ma-upsell__card';

    const safeTitle = escapeHtml(product.title);
    const safeUrl = escapeHtml(product.url);

    const imageMarkup = product.featured_image
      ? `<img src="${escapeHtml(product.featured_image)}" alt="${safeTitle}" loading="lazy">`
      : `<span class="ma-upsell__card-placeholder" aria-hidden="true">${escapeHtml(
          product.title.charAt(0)
        )}</span>`;

    item.innerHTML = `<a href="${safeUrl}" class="ma-upsell__card-image" aria-label="${maAttr(
      maFill(MA_S.viewProduct, { __PRODUCT__: safeTitle })
    )}">${imageMarkup}</a><a href="${safeUrl}" class="ma-upsell__card-title-link"><div class="ma-upsell__card-body"><p class="ma-upsell__card-title">${safeTitle}</p><p class="ma-upsell__card-price">${formatMoneyCents(
      product.price
    )}</p></div></a><button type="button" class="ma-upsell__card-button" data-variant-id="${
      variant.id
    }">${maText(MA_S.addShort)}</button>`;

    item
      .querySelector('.ma-upsell__card-button')
      ?.addEventListener('click', (event) => this.handleUpsellAdd(event));

    return item;
  }

  /** Produits deja connus, dans l'ordre demande. */
  #resolveFromCache(handles) {
    return handles
      .map((handle) => this.#productCache.get(handle))
      .filter((product) => Boolean(product));
  }

  /** Charge les produits absents du cache, puis reapplique les blocs. */
  async #ensureProducts(handles) {
    const missing = handles.filter((handle) => handle && !this.#productCache.has(handle));
    if (missing.length === 0) return false;

    const products = await Promise.all(missing.map(fetchProductByHandle));

    missing.forEach((handle, index) => {
      // On memorise aussi les echecs : pas de martelement du serveur.
      this.#productCache.set(handle, products[index] || null);
    });

    return true;
  }

  /** Relit le panier et les produits necessaires, puis met a jour l'affichage. */
  async refresh() {
    if (this.#refreshing) {
      this.#refreshQueued = true;
      return;
    }

    this.#refreshing = true;

    try {
      const cart = await fetchCart();
      if (cart) this.#cart = cart;

      this.#applyBranding();

      const wanted = [...UPSELL_HANDLES];
      if (PACK_HANDLE) wanted.push(PACK_HANDLE);

      const loaded = await this.#ensureProducts(wanted);
      if (loaded) this.#applyBranding();
    } finally {
      this.#refreshing = false;

      if (this.#refreshQueued) {
        this.#refreshQueued = false;
        this.refresh();
      }
    }
  }

  async handleUpsellAdd(event) {
    const button = event.currentTarget;
    const variantId = button?.dataset.variantId;

    if (!variantId || button.disabled) return;

    const originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = '…';

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const response = await fetch(`${MA_ROOT}cart/add.js`, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/javascript' },
      });

      if (!response.ok) throw new Error(`statut ${response.status}`);

      const cart = await fetchCart();
      if (cart) this.#cart = cart;

      // Mise a jour immediate de nos blocs : l'evenement diffuse ensuite porte
      // notre signature et sera ignore par #onCartUpdate.
      this.#applyBranding();
      dispatchCartUpdate(cart);

      button.textContent = MA_S.addedShort || '✓';
      window.setTimeout(() => {
        button.textContent = originalLabel;
        button.disabled = false;
      }, 1200);
    } catch (error) {
      console.error('[ma-cart-drawer] ajout depuis la vente complementaire en echec', error);
      button.textContent = MA_S.addFailed || '!';
      window.setTimeout(() => {
        button.textContent = originalLabel;
        button.disabled = false;
      }, 1500);
    }
  }
}

const maCartEnhancements = new MaisonAtlasCartEnhancements();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => maCartEnhancements.init());
} else {
  maCartEnhancements.init();
}
